import ArrowDownRight from "lucide-react/dist/esm/icons/arrow-down-right";
import ArrowUpRight from "lucide-react/dist/esm/icons/arrow-up-right";
import Plus from "lucide-react/dist/esm/icons/plus";
import Globe2 from "lucide-react/dist/esm/icons/globe-2";
import GripVertical from "lucide-react/dist/esm/icons/grip-vertical";
import MousePointer2 from "lucide-react/dist/esm/icons/mouse-pointer-2";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import Sparkles from "lucide-react/dist/esm/icons/sparkles";
import Zap from "lucide-react/dist/esm/icons/zap";
import {
  AccordionGallery,
  DepthText,
  PixelSwap,
  ScrollExpand,
} from "./ReactBits";
import {
  EditableIcon,
  EditableImage,
  EditableLink,
  EditableText,
} from "./Editable";
import { itemsOf } from "../lib";
import { useEditor } from "../store";
import type { Item, Section } from "../types";
import { useCountUp } from "../hooks";

const iconMap = {
  MousePointer2,
  Zap,
  ShieldCheck,
  Globe2,
  Sparkles,
  ArrowDownRight,
  ArrowUpRight,
  Plus,
};

const cardStyle = (item: Item): React.CSSProperties => ({
  background: item.background,
  borderRadius: item.cardRadius,
  boxShadow: item.cardShadow ? `0 ${item.cardShadow / 2}px ${item.cardShadow}px rgb(0 0 0 / .14)` : undefined,
  padding: item.spacing,
  textAlign: item.cardAlign,
});

function SectionIcon({ section, field }: { section: Section; field: string }) {
  const Icon =
    iconMap[String(section.content[field]) as keyof typeof iconMap] ?? Sparkles;
  return (
    <EditableIcon section={section} field={field}>
      <Icon />
    </EditableIcon>
  );
}

function ItemIcon({
  section,
  item,
  fallback = "Sparkles",
}: {
  section: Section;
  item: Item;
  fallback?: keyof typeof iconMap;
}) {
  const Icon = iconMap[item.icon as keyof typeof iconMap] ?? iconMap[fallback];
  return (
    <EditableIcon section={section} item={item} field="icon">
      <Icon />
    </EditableIcon>
  );
}

function Heading({
  section,
  intro = true,
}: {
  section: Section;
  intro?: boolean;
}) {
  return (
    <header className={`section-heading align-${section.style.align}`}>
      <EditableText
        section={section}
        field="kicker"
        as="span"
        className="kicker"
      />
      <EditableText section={section} field="title" as="h2" />
      {intro && section.content.intro != null && (
        <EditableText section={section} field="intro" as="p" />
      )}
    </header>
  );
}

export function Hero({ section }: { section: Section }) {
  const { editing, preview, setEditing, exitEditing, select, selection } = useEditor();
  const titleElementId = `${section.id}:section:title`;
  const titleAlign = String(section.content.titleAlign ?? "left");
  return (
    <div className="hero-shell">
      <div className="hero-topline">
        <EditableText section={section} field="eyebrow" as="span" />
        <span>
          <EditableText section={section} field="scrollLabel" as="span" />
          <SectionIcon section={section} field="scrollIcon" />
        </span>
      </div>
      <div className="hero-grid">
        <div
          className="hero-title-wrap"
          style={{
            justifyContent:
              titleAlign === "center"
                ? "center"
                : titleAlign === "right"
                  ? "flex-end"
                  : "flex-start",
          }}
        >
          <div
            className="depth-positioner"
            style={{
              width: "100%",
              maxWidth: Number(section.content.titleMaxWidth ?? 1180),
              translate: `${Number(section.content.titleOffsetX ?? 0)}px ${Number(section.content.titleOffsetY ?? 0)}px`,
              textAlign: titleAlign as "left" | "center" | "right",
            }}
          >
          <DepthText
            text={String(section.content.title)}
            layers={Number(section.content.layers)}
            depth={Number(section.content.depth)}
            faceColor={String(section.content.faceColor)}
            depthColor={String(section.content.depthColor)}
            tilt={Number(section.content.tilt)}
            perspective={Number(section.content.perspective)}
            pointerTracking={Boolean(section.content.pointerTracking)}
            smoothing={Number(section.content.smoothing)}
            autoOrbit={Boolean(section.content.orbit)}
            orbitSpeed={Number(section.content.orbitSpeed)}
            fontSize={Number(section.content.fontSize)}
            fontWeight={Number(section.content.fontWeight)}
            shadow={Boolean(section.content.shadow)}
          />
          </div>
          {editing && !preview && (
            <button
              className={`depth-edit-hit ${selection?.elementId === titleElementId ? "cms-selected" : ""}`}
              data-cms-id={titleElementId}
              data-cms-kind="text"
              data-cms-section={section.id}
              data-cms-field="title"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                select({
                  sectionId: section.id,
                  kind: "text",
                  field: "title",
                  elementId: titleElementId,
                });
              }}
              aria-label="Edit hero title"
            />
          )}
        </div>
        <div className="hero-copy">
          <EditableText section={section} field="intro" as="p" />
          <span className="hero-flair" aria-hidden="true">✦</span>
        </div>
        <EditableImage
          section={section}
          className="hero-image"
          loading="eager"
        />
        <div className="hero-note">
          <button onClick={() => (editing ? exitEditing() : setEditing(true))}>
            <SectionIcon section={section} field="noteIcon" />
            <EditableText
              section={section}
              field={editing ? "viewLabel" : "editLabel"}
              as="span"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export function Services({ section }: { section: Section }) {
  const { editing, reorderItems, select, selection } = useEditor();
  const items = itemsOf(section);
  return (
    <div className="container">
      <Heading section={section} />
      <div
        className="service-grid"
        style={{
          gridTemplateColumns: `repeat(${section.style.columns}, minmax(0,1fr))`,
          gap: section.style.gap,
        }}
      >
        {items.map((item) => {
          const Icon = iconMap[item.icon as keyof typeof iconMap] ?? Sparkles;
          return (
            <article
              key={item.id}
              data-cms-id={`${section.id}:${item.id}:item`}
              data-cms-kind="item"
              data-cms-section={section.id}
              data-cms-item={item.id}
              style={cardStyle(item)}
              className={selection?.itemId === item.id ? "cms-selected" : ""}
              draggable={editing}
              onDragStart={(e) => e.dataTransfer.setData("text/plain", item.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) =>
                reorderItems(
                  section.id,
                  e.dataTransfer.getData("text/plain"),
                  item.id,
                )
              }
              onClick={() =>
                editing &&
                select({ sectionId: section.id, kind: "item", itemId: item.id })
              }
            >
              {editing && <GripVertical className="item-grip" size={16} />}
              <EditableIcon
                section={section}
                item={item}
                className="service-icon"
              >
                <Icon />
              </EditableIcon>
              <EditableText
                section={section}
                item={item}
                field="label"
                as="span"
                className="service-no"
              />
              <EditableText
                section={section}
                item={item}
                field="title"
                as="h3"
              />
              <EditableText section={section} item={item} field="text" as="p" />
            </article>
          );
        })}
      </div>
    </div>
  );
}

export function PixelSwapSection({ section }: { section: Section }) {
  const { editing, preview } = useEditor();
  return (
    <div className="container pixel-layout">
      <Heading section={section} intro={false} />
      <PixelSwap
        firstContent={
          <div className="pixel-state pixel-state-before">
            <EditableText section={section} field="beforeLabel" as="span" />
            <EditableText section={section} field="beforeTitle" as="strong" />
            <EditableText section={section} field="beforeBody" as="p" />
          </div>
        }
        secondContent={
          <div className="pixel-state pixel-state-after">
            <EditableText section={section} field="afterLabel" as="span" />
            <EditableText section={section} field="afterTitle" as="strong" />
            <EditableText section={section} field="afterBody" as="p" />
          </div>
        }
        pixelSize={Number(section.content.pixelSize)}
        gap={Number(section.content.gap)}
        pixelRadius={Number(section.content.pixelRadius)}
        pixelSpin={Number(section.content.spin)}
        pixelScale={Number(section.content.scale)}
        duration={Number(section.content.duration)}
        pixelDuration={Number(section.content.pixelDuration)}
        pattern={String(section.content.pattern) as "diagonal"}
        randomness={Number(section.content.randomness)}
        fade={Boolean(section.content.fade)}
        easing={String(
          section.content.easing ?? "cubic-bezier(0.22, 1, 0.36, 1)",
        )}
        trigger={
          String(section.content.trigger) as "hover" | "click" | "manual"
        }
        aspectRatio={String(section.content.aspectRatio ?? "16 / 10")}
        editing={editing && !preview}
        hintContent={
          <EditableText section={section} field="hoverHint" as="span" />
        }
      />
    </div>
  );
}

export function Gallery({ section }: { section: Section }) {
  const { editing, preview, select, selection, reorderItems } = useEditor();
  return (
    <div className="container wide">
      <Heading section={section} />
      <AccordionGallery
        items={itemsOf(section)}
        grayscale={Boolean(section.content.grayscale)}
        parallax={Number(section.content.parallax)}
        tilt={Number(section.content.tilt)}
        gap={section.style.gap}
        radius={section.style.radius}
        expandRatio={Number(section.content.expandRatio)}
        orientation={String(section.content.orientation)}
        duration={Number(section.content.timing)}
        trigger={String(section.content.trigger)}
        selectedId={selection?.itemId}
        onSelect={(itemId) =>
          select({ sectionId: section.id, kind: "item", itemId })
        }
        onReorder={(a, o) => reorderItems(section.id, a, o)}
        editing={editing && !preview}
        renderImage={(item) => (
          <EditableImage section={section} item={item} />
        )}
        renderNumber={(item) => (
          <EditableText section={section} item={item} field="label" as="span" />
        )}
        renderCaption={(item) => (
          <>
            <EditableText section={section} item={item} field="text" as="small" />
            <EditableText section={section} item={item} field="title" as="strong" />
          </>
        )}
      />
    </div>
  );
}

function Stat({ section, item }: { section: Section; item: Item }) {
  const ref = useCountUp(item.value ?? "0");
  const { editing, preview, selection, select, reorderItems } = useEditor();
  return (
    <article
      className={selection?.itemId === item.id ? "cms-selected" : ""}
      data-cms-id={`${section.id}:${item.id}:item`}
      data-cms-kind="item"
      data-cms-section={section.id}
      data-cms-item={item.id}
      style={cardStyle(item)}
      draggable={editing && !preview}
      onDragStart={(event) => event.dataTransfer.setData("text/plain", item.id)}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => reorderItems(section.id, event.dataTransfer.getData("text/plain"), item.id)}
      onClick={() => editing && !preview && select({ sectionId: section.id, kind: "item", itemId: item.id })}
    >
      {editing && !preview ? (
        <EditableText section={section} item={item} field="value" as="span" className="stat-value" />
      ) : (
        <span className="stat-value" ref={ref}>{item.value}</span>
      )}
      <EditableText section={section} item={item} field="title" as="h3" />
      <EditableText section={section} item={item} field="text" as="p" />
    </article>
  );
}
export function Stats({ section }: { section: Section }) {
  return (
    <div className="container">
      <Heading section={section} intro={false} />
      <div className="stats-grid">
        {itemsOf(section).map((item) => (
          <Stat key={item.id} section={section} item={item} />
        ))}
      </div>
    </div>
  );
}

export function ScrollExpandSection({ section }: { section: Section }) {
  const { editing, preview } = useEditor();
  return (
    <ScrollExpand
      mediaContent={<EditableImage section={section} />}
      titleContent={<EditableText section={section} field="title" as="h2" />}
      hintContent={<EditableText section={section} field="hint" as="span" />}
      editing={editing && !preview}
      startWidth={Number(section.content.startWidth)}
      startHeight={Number(section.content.startHeight)}
      startRadius={Number(section.content.startRadius)}
      endRadius={Number(section.content.endRadius)}
      mediaZoom={Number(section.content.mediaZoom)}
      scrollDistance={Number(section.content.scrollDistance)}
      smoothing={Number(section.content.smoothing)}
      overlay={Number(section.content.overlay)}
    />
  );
}

export function Testimonials({ section }: { section: Section }) {
  const { editing, preview, selection, select, reorderItems } = useEditor();
  return (
    <div className="container">
      <Heading section={section} intro={false} />
      <div className="quote-grid">
        {itemsOf(section).map((item) => (
          <figure
            key={item.id}
            className={selection?.itemId === item.id ? "cms-selected" : ""}
            data-cms-id={`${section.id}:${item.id}:item`}
            data-cms-kind="item"
            data-cms-section={section.id}
            data-cms-item={item.id}
            style={cardStyle(item)}
            draggable={editing && !preview}
            onDragStart={(event) => event.dataTransfer.setData("text/plain", item.id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => reorderItems(section.id, event.dataTransfer.getData("text/plain"), item.id)}
            onClick={() => editing && !preview && select({ sectionId: section.id, kind: "item", itemId: item.id })}
          >
            <EditableText section={section} item={item} field="label" as="span" />
            <EditableText
              section={section}
              item={item}
              field="title"
              as="blockquote"
            />
            <EditableText
              section={section}
              item={item}
              field="text"
              as="figcaption"
            />
          </figure>
        ))}
      </div>
    </div>
  );
}

const dayIndex = (new Date().getDay() + 6) % 7;
export function Hours({ section }: { section: Section }) {
  const { editing, select, selection } = useEditor();
  const items = itemsOf(section);
  const today = items[dayIndex];
  const hour = new Date().getHours() + new Date().getMinutes() / 60;
  const open =
    today?.value === "open" &&
    hour >= Number(today.title?.split(":")[0]) &&
    hour < Number(today.text?.split(":")[0]);
  return (
    <div className="container hours-layout">
      <div>
        <Heading section={section} />
        <div className={`open-status ${open ? "open" : ""}`}>
          <i />
          <EditableText
            section={section}
            field={open ? "openLabel" : "closedLabel"}
            as="span"
          />
          {open && today && (
            <EditableText section={section} item={today} field="text" as="span" />
          )}
        </div>
      </div>
      <div className="hours-list">
        {items.map((item, index) => (
          <button
            key={item.id}
            data-cms-id={`${section.id}:${item.id}:item`}
            data-cms-kind="item"
            data-cms-section={section.id}
            data-cms-item={item.id}
            style={cardStyle(item)}
            draggable={editing}
            onDragStart={(event) => event.dataTransfer.setData("text/plain", item.id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => useEditor.getState().reorderItems(section.id, event.dataTransfer.getData("text/plain"), item.id)}
            onClick={() =>
              editing &&
              select({ sectionId: section.id, kind: "item", itemId: item.id })
            }
            className={`${selection?.itemId === item.id ? "cms-selected" : ""} ${index === dayIndex ? "today" : ""}`}
          >
            <span>
              <EditableText section={section} item={item} field="label" as="span" />
              {index === dayIndex && (
                <EditableText section={section} field="todayLabel" as="small" />
              )}
            </span>
            <strong>
              <EditableText section={section} item={item} field="title" as="span" />
              {item.value !== "closed" && (
                <>
                  {" — "}
                  <EditableText section={section} item={item} field="text" as="span" />
                </>
              )}
            </strong>
            {item.category && (
              <EditableText section={section} item={item} field="category" as="em" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Blog({ section }: { section: Section }) {
  const items = itemsOf(section);
  const { editing, preview, selection, select, reorderItems } = useEditor();
  return (
    <div className="container">
      <Heading section={section} intro={false} />
      <div className={`blog-grid layout-${section.layout}`}>
        {items.map((item, index) => (
          <article
            key={item.id}
            className={`${index === 0 ? "featured" : ""} ${selection?.itemId === item.id ? "cms-selected" : ""}`}
            data-cms-id={`${section.id}:${item.id}:item`}
            data-cms-kind="item"
            data-cms-section={section.id}
            data-cms-item={item.id}
            style={cardStyle(item)}
            draggable={editing && !preview}
            onDragStart={(event) => event.dataTransfer.setData("text/plain", item.id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => reorderItems(section.id, event.dataTransfer.getData("text/plain"), item.id)}
            onClick={() => editing && !preview && select({ sectionId: section.id, kind: "item", itemId: item.id })}
          >
            <EditableImage section={section} item={item} />
            <div>
              <span className="post-meta">
                <EditableText
                  section={section}
                  item={item}
                  field="category"
                  as="b"
                />{" "}
                ·{" "}
                <EditableText
                  section={section}
                  item={item}
                  field="date"
                  as="time"
                />
              </span>
              <EditableText
                section={section}
                item={item}
                field="title"
                as="h3"
              />
              <EditableText section={section} item={item} field="text" as="p" />
              <EditableLink section={section} item={item} field="label" linkField="link" className="post-link">
                <EditableText section={section} item={item} field="label" as="span" />
                <ItemIcon section={section} item={item} fallback="ArrowUpRight" />
              </EditableLink>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export function FAQ({ section }: { section: Section }) {
  const { editing, preview, selection, select, reorderItems } = useEditor();
  return (
    <div className="container faq-layout">
      <Heading section={section} intro={false} />
      <div className="faq-list">
        {itemsOf(section).map((item, index) => (
          <details
            key={item.id}
            open={index === 0}
            className={selection?.itemId === item.id ? "cms-selected" : ""}
            data-cms-id={`${section.id}:${item.id}:item`}
            data-cms-kind="item"
            data-cms-section={section.id}
            data-cms-item={item.id}
            style={cardStyle(item)}
            draggable={editing && !preview}
            onDragStart={(event) => event.dataTransfer.setData("text/plain", item.id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => reorderItems(section.id, event.dataTransfer.getData("text/plain"), item.id)}
            onClick={() => editing && !preview && select({ sectionId: section.id, kind: "item", itemId: item.id })}
          >
            <summary>
              <EditableText
                section={section}
                item={item}
                field="title"
                as="span"
              />
              <ItemIcon section={section} item={item} fallback="Plus" />
            </summary>
            <EditableText section={section} item={item} field="text" as="p" />
          </details>
        ))}
      </div>
    </div>
  );
}

export function CTA({ section }: { section: Section }) {
  const setEditing = useEditor((s) => s.setEditing);
  return (
    <div className="container cta-inner">
      <EditableText
        section={section}
        field="kicker"
        as="span"
        className="kicker"
      />
      <EditableText section={section} field="title" as="h2" />
      <EditableText section={section} field="intro" as="p" />
      <div>
        <button className="button dark" onClick={() => setEditing(true)}>
          <EditableText section={section} field="button" as="span" />
          <SectionIcon section={section} field="buttonIcon" />
        </button>
        <EditableLink section={section} field="secondary" linkField="secondaryLink" className="text-button">
          <EditableText section={section} field="secondary" as="span" />
        </EditableLink>
      </div>
    </div>
  );
}

export function Content({ section }: { section: Section }) {
  return (
    <div className={`container content-block ${section.layout}`}>
      <div>
        <Heading section={section} />
        {Boolean(section.content.showButton) && (
          <EditableLink
            section={section}
            field="button"
            className="button dark"
          >
            <EditableText section={section} field="button" as="span" />
            <SectionIcon section={section} field="buttonIcon" />
          </EditableLink>
        )}
      </div>
      {Boolean(section.content.showImage) && (
        <EditableImage section={section} className="content-image" />
      )}
    </div>
  );
}

export const SECTION_REGISTRY = {
  hero: Hero,
  content: Content,
  services: Services,
  "pixel-swap": PixelSwapSection,
  gallery: Gallery,
  stats: Stats,
  "scroll-expand": ScrollExpandSection,
  testimonials: Testimonials,
  hours: Hours,
  blog: Blog,
  faq: FAQ,
  cta: CTA,
};
